import { NgClass, NgIf } from "@angular/common";
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { LoginVO, RegistrierungVO, TeilnehmerApiService, TeilnehmerVO, UserInfoVO } from "@core/api/v1";
import { Monate } from "@core/model/monate";
import { LoginService } from "@core/services/login/login.service";
import { NotificationService } from "@core/services/notification/notification.service";
import { DateValidator } from "@shared/validators/date-validator";
import { PasswordValidator } from "@shared/validators/password-validator";
import { PhoneNumberValidator } from "@shared/validators/phoneNumberValidator";
import { CheckboxModule } from "primeng/checkbox";
import { DropdownModule } from "primeng/dropdown";
import { InputMaskModule } from "primeng/inputmask";
import { InputTextModule } from "primeng/inputtext";
import { ListboxModule } from "primeng/listbox";
import { PaginatorModule } from "primeng/paginator";
import AnredeEnum = UserInfoVO.AnredeEnum;
import BundeslandEnum = TeilnehmerVO.BundeslandEnum;
import BerufEnum = TeilnehmerVO.BerufEnum;
import { anredeOptionen } from "@core/model/optionen/anrede";
import { bundeslandOptionen } from "@core/model/optionen/bundesland";
import { berufOptionen } from "@core/model/optionen/beruf";
import { fachrichtungOptionen } from "@core/model/optionen/fachrichtung";
import { gutachterTaetigkeitOptionen } from "@core/model/optionen/gutachterTaetigkeit";

@Component({
    selector: 'app-teilnehmer-verwalten',
    standalone: true,
    imports: [
        CheckboxModule,
        DropdownModule,
        InputMaskModule,
        InputTextModule,
        ListboxModule,
        NgIf,
        PaginatorModule,
        ReactiveFormsModule,
        NgClass,
    ],
    templateUrl: './teilnehmer-verwalten.component.html',
    styleUrl: './teilnehmer-verwalten.component.scss',
})
export class TeilnehmerVerwaltenComponent implements OnInit {
    isLoggedIn: boolean;

    protected readonly anredeOptionen = anredeOptionen;

    protected readonly bundeslandOptionen = bundeslandOptionen;

    protected readonly berufOptionen = berufOptionen;

    protected readonly fachrichtungOptionen = fachrichtungOptionen;

    protected readonly gutachterTaetigkeitOptionen = gutachterTaetigkeitOptionen;

    protected readonly monate = Monate;

    registrierungVO!: RegistrierungVO;
    toUpdateTeilnehmer: TeilnehmerVO;
    teilnehmer: TeilnehmerVO;

    formular = this.formbuilder.group({
        email: ['', [Validators.required, Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,6}$")]],
        passwort: [''],
        pwBestaetigen: [''],
        anrede: [null, Validators.required],
        akadGradVorangestellt: [''],
        akadGradNachgestellt: [''],

        vorname: ['', [Validators.required, this.notBlankValidator()]],
        nachname: ['', [Validators.required, this.notBlankValidator()]],

        geburtsdatum: this.formbuilder.group({
            geburtstag: [null, [Validators.required, Validators.pattern(/^[0-9]\d*$/)]],
            geburtsmonat: ['', Validators.required],
            geburtsjahr: [null, [Validators.required, Validators.pattern(/^[0-9]\d*$/)]],
        }, { validators: DateValidator.isDateValid('geburtstag', 'geburtsmonat', 'geburtsjahr') }),
        adresse: ['', [Validators.required, this.notBlankValidator()]],
        adresszusatz: [''],
        plz: [null, [Validators.required, Validators.pattern(/^[0-9]\d*$/), Validators.minLength(4)]],
        ort: ['', [Validators.required, this.notBlankValidator()]],
        bundesland: [null, Validators.required],
        festnetz: ['', Validators.pattern(/^00+[0-9]{2}[1-5,7-9]\d*$/)],
        mobil: ['', Validators.pattern(/^00+[0-9]{2}6\d*$/)],
        berufsgruppe: [null, Validators.required],
        fachrichtung: [[]],
        arztnummer: [''],
        gutachterFuer: [[], [Validators.required, this.keinAusgewaehltValidator()]],
        einverstaendnis: [false],
    }, {
        validators: [
            PhoneNumberValidator.atLeastOneFilled('festnetz', 'mobil'),
            PasswordValidator.areEqual('passwort', 'pwBestaetigen'),
        ],
    });

    @Output() profilAktualisiertEvent = new EventEmitter();

    istArzt: boolean = false;
    istFachrichtungTouched: boolean = false;
    istGutachterTouched: boolean = false;
    gutachterInvalidMessage: string;
    fachrichtungInvalidMessage: string;

    constructor(private formbuilder: FormBuilder,
                private router: Router,
                private notificationService: NotificationService,
                private teilnehmerService: TeilnehmerApiService,
                private loginService: LoginService) {
    }

    ngOnInit() {
        this.loginUeberpruefen();

        if (!this.isLoggedIn) {
            // registrierung
            this.formular.controls['passwort'].setValidators([Validators.required, Validators.minLength(7)]);
            this.formular.controls['pwBestaetigen'].setValidators([Validators.required]);
            this.formular.controls['einverstaendnis'].setValidators([Validators.requiredTrue]);
        } else {
            // profil bearbeiten
            this.teilnehmerService.holeTeilnehmer().subscribe(data => {
                if (data.anrede == null) {
                    data.anrede = AnredeEnum.HERR;
                }

                this.toUpdateTeilnehmer = data;

                let monat = Monate.filter(i => i.value === Number(data.geburtsdatum?.split("-")[1]!))[0];
                if (! monat) {
                    monat = Monate[0];
                }

                let akadGradVorangestellt = data.vorgestellterTitel;
                if (! akadGradVorangestellt) {
                    akadGradVorangestellt = '';
                }
                akadGradVorangestellt = akadGradVorangestellt.trim();

                let akadGradNachgestellt = data.nachgestellterTitel;
                if (! akadGradNachgestellt) {
                    akadGradNachgestellt = '';
                }
                akadGradNachgestellt = akadGradNachgestellt.trim();

                let adresse = data.adresse;
                if (! adresse) {
                    adresse = '';
                }
                adresse = adresse.trim();

                let adresszusatz = data.adresszusatz;
                if (! adresszusatz) {
                    adresszusatz = '';
                }
                adresszusatz = adresszusatz.trim();

                let plz = data.postleitzahl;
                if (! plz) {
                    plz = '';
                }
                plz = plz.trim();

                let ort = data.ort;
                if (! ort) {
                    ort = '';
                }
                ort = ort.trim();

                let bundesland;
                if (data.bundesland) {
                    bundesland = data.bundesland;
                }
                else {
                    bundesland = BundeslandEnum.WIEN;
                }

                let festnetz = data.telefon;
                if (! festnetz) {
                    festnetz = '';
                }
                festnetz = festnetz.trim();

                let mobil = data.mobilTelefon;
                if (! mobil) {
                    mobil = '';
                }
                mobil = mobil.trim();

                let arztNummer: string | undefined | null;

                if (data.beruf == "ARZT") {
                    arztNummer = data.oeaekNummer;
                    if (! arztNummer) {
                        arztNummer = '';
                    }
                } else {
                    arztNummer = null;
                }

                this.formular.setValue({
                    email: data.emailAdresse!,
                    passwort: '',
                    pwBestaetigen: '',
                    anrede: data.anrede as any,
                    akadGradVorangestellt: akadGradVorangestellt,
                    akadGradNachgestellt: akadGradNachgestellt,
                    vorname: data.vorname!,
                    nachname: data.nachname!,
                    geburtsdatum: {
                        geburtstag: data.geburtsdatum ? data.geburtsdatum?.split("-")[2]! : '1' as any,
                        geburtsmonat: monat.value as any,
                        geburtsjahr: data.geburtsdatum ? data.geburtsdatum?.split("-")[0]! : '' as any,
                    },
                    adresse: adresse,
                    adresszusatz: adresszusatz,
                    plz: plz as any,
                    ort: ort,
                    bundesland: bundesland as any,
                    festnetz: festnetz,
                    mobil: mobil,
                    berufsgruppe: data.beruf! as any,
                    fachrichtung: data.fachrichtungen as any,
                    arztnummer: arztNummer!,
                    gutachterFuer: data.gutachterTaetigkeiten as any,
                    einverstaendnis: true,
                });

                if (data.beruf !== "ARZT") {
                    this.formular.get('arztnummer')?.setValue('');
                }
            });
            this.formular.markAllAsTouched();
        }

        this.formular.get('gutachterFuer')?.valueChanges.subscribe(val => {
            this.istGutachterTouched = true;

            if (this.formular.controls.gutachterFuer.hasError('keinIstAusgewaehlt')) {
                this.gutachterInvalidMessage = "'Keine Gutachtertätigkeit' ist mitausgewählt."
            }
            else {
                this.gutachterInvalidMessage = "Bitte geben Sie Ihre Gutachtertätigkeit an."
            }
        });

        this.formular.get('berufsgruppe')?.valueChanges.subscribe(val => {
            if (val === 'ARZT') {
                this.formular.controls['arztnummer'].setValidators([Validators.required]);
                this.formular.controls['fachrichtung'].setValidators([Validators.required, this.keinAusgewaehltValidator()]);
                this.formular.controls['akadGradVorangestellt'].setValidators([
                    Validators.required,
                    this.notBlankValidator()
                ]);
            } else {
                this.formular.controls['arztnummer'].clearValidators();
                this.formular.controls['fachrichtung'].clearValidators();
                this.formular.controls['akadGradVorangestellt'].clearValidators();
            }
            this.istArzt = (val === 'ARZT');

            if (!this.istArzt) {
                this.formular.controls['fachrichtung'].reset();
                this.formular.controls['arztnummer'].setValue('');
            }

            this.formular.controls['arztnummer'].updateValueAndValidity();
            this.formular.controls['fachrichtung'].updateValueAndValidity();
            this.formular.controls['akadGradVorangestellt'].updateValueAndValidity();
            this.istFachrichtungTouched = false;
        });

        this.formular.get('fachrichtung')?.valueChanges.subscribe(val => {
            this.istFachrichtungTouched = true;

            if (this.formular.controls.fachrichtung.hasError('keinIstAusgewaehlt')) {
                this.fachrichtungInvalidMessage = "'Keine Fachrichtung' ist mitausgewählt.";
            }
            else {
                this.fachrichtungInvalidMessage = "Bitte geben Sie Ihre Fachrichtung(en) an.";
            }
        });
    }

    notBlankValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            if (value == null) return null;

            if (typeof value === 'string') {
                return value.trim().length === 0 ? { notBlank: true } : null;
            }

            return null;
        };
    }

    keinAusgewaehltValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            if (value !== null && value.includes('KEINE') && value.length > 1) {
                return { keinIstAusgewaehlt: true };
            }

            return null;
        };
    }

    loginUeberpruefen() {
        this.loginService.isLoggedIn.subscribe((data) => {
            this.isLoggedIn = data;
        });
    }

    private trimFields(paths: string[]) {
        paths.forEach(p => {
            const c = this.formular.get(p);
            if (c && typeof c.value === 'string') {
                c.setValue(c.value.trim(), { emitEvent: false });
            }
        });
    }

    speichern() {
        this.trimFields([
            'vorname',
            'nachname',
            'akadGradVorangestellt',
            'akadGradNachgestellt',
            'adresse',
            'adresszusatz',
            'ort',
        ]);

        if (this.formular.invalid) {
            this.formular.markAllAsTouched();
            return;
        }

        if (this.isLoggedIn) {
            this.aenderungenSpeichern();
        } else {
            this.neuenTeilnehmerSpeichern();
        }
    }

    neuenTeilnehmerSpeichern() {
        const formValue = this.formular.value;
        const birthdateForm = formValue.geburtsdatum;
        const monthNr = String(birthdateForm?.geburtsmonat!).padStart(2, '0');
        const dayNr = String(birthdateForm?.geburtstag!).padStart(2, '0');
        const yearNr = String(birthdateForm?.geburtsjahr!).padStart(4, '0');
        const birthdate = yearNr + "-" + monthNr + "-" + dayNr;

        this.registrierungVO = {
            emailAdresse: formValue.email!,
            passwort: formValue.passwort!,
            anrede: formValue.anrede!,
            vorname: formValue.vorname!,
            nachname: formValue.nachname!,
            vorgestellterTitel: formValue.akadGradVorangestellt!,
            nachgestellterTitel: formValue.akadGradNachgestellt!,
            geburtsdatum: birthdate,
            adresse: formValue.adresse!,
            adresszusatz: formValue.adresszusatz!,
            postleitzahl: formValue.plz!,
            ort: formValue.ort!,
            bundesland: formValue.bundesland!,
            telefon: formValue.festnetz!,
            mobilTelefon: formValue.mobil!,
            beruf: formValue.berufsgruppe!,
            fachrichtungen: formValue.fachrichtung!,
            oeaekNummer: formValue.arztnummer!,
            gutachterTaetigkeiten: formValue.gutachterFuer!,
        };

        this.registriereUser();
    }

    registriereUser() {
        const formValue = this.formular.value;

        this.teilnehmerService.erstelleTeilnehmer(this.registrierungVO).subscribe(response => {
            if (response.id != null) {
                const loginVO: LoginVO = {
                    username: formValue.email!,
                    password: formValue.passwort!,
                };
                this.loginService.login(loginVO);
                this.notificationService.showSuccess("Erfolgreich registriert");
                this.router.navigate(["/"]);
            }
        }, error => {
            this.scrollToTop();
            this.notificationService.showError("E-Mail oder Arztnummer wird bereits verwendet.");
        });
    }

    aenderungenSpeichern() {
        const formValue = this.formular.value;
        const birthdateForm = formValue.geburtsdatum;
        const monthNr = String(birthdateForm?.geburtsmonat!).padStart(2, '0');
        const dayNr = String(birthdateForm?.geburtstag!).padStart(2, '0');
        const yearNr = String(birthdateForm?.geburtsjahr!).padStart(4, '0');
        const birthdate = yearNr + "-" + monthNr + "-" + dayNr;

        this.teilnehmer = {
            id: this.toUpdateTeilnehmer.id,
            emailAdresse: formValue.email!,
            anrede: formValue.anrede! as AnredeEnum,
            vorname: formValue.vorname!,
            nachname: formValue.nachname!,
            vorgestellterTitel: formValue.akadGradVorangestellt!,
            nachgestellterTitel: formValue.akadGradNachgestellt!,
            geburtsdatum: birthdate,
            adresse: formValue.adresse!,
            adresszusatz: formValue.adresszusatz!,
            postleitzahl: formValue.plz!,
            ort: formValue.ort!,
            bundesland: formValue.bundesland! as BundeslandEnum,
            telefon: formValue.festnetz!,
            mobilTelefon: formValue.mobil!,
            beruf: formValue.berufsgruppe! as BerufEnum,
            fachrichtungen: formValue.fachrichtung! as any,
            oeaekNummer: formValue.arztnummer!,
            gutachterTaetigkeiten: formValue.gutachterFuer! as any,
        };

        this.teilnehmerService.updateTeilnehmer(this.teilnehmer).subscribe(response => {
            this.loginService.refreshUser();
            this.notificationService.showSuccess("Änderungen gespeichert");
            this.router.navigate(["/"]);
        }, error => {
            this.scrollToTop();
            this.notificationService.showError("E-Mail oder Arztnummer wird bereits verwendet.");
        });
    }

    scrollToTop() {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    }
}
